import { useRef, type MouseEvent } from 'react';
import { Pressable } from 'react-native';

import { useLlmPrivateBlock } from '@/lib/llm-private/use-llm-private-block';

type PrivateFlagButtonProps = {
  blockId: string;
  disabled?: boolean;
  className?: string;
};

function LockIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={`pointer-events-none ${filled ? 'text-warning' : 'text-fg-muted'}`}
    >
      <rect x="5" y="11" width="14" height="10" rx="2" fill={filled ? 'currentColor' : 'none'} />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" />
    </svg>
  );
}

export function PrivateFlagButton({ blockId, disabled, className }: PrivateFlagButtonProps) {
  const { isPrivate, toggling, toggle } = useLlmPrivateBlock(blockId);
  const skipClick = useRef(false);

  return (
    <Pressable
      aria-busy={toggling}
      aria-label={isPrivate ? 'Remove private' : 'Mark as private'}
      aria-pressed={isPrivate}
      className={`flex h-8 w-8 items-center justify-center rounded-md hover:bg-surface-raised ${
        className ?? ''
      }`}
      disabled={disabled}
      style={{ zIndex: 2 }}
      title={isPrivate ? 'Private — hidden from assistants' : 'Mark as private'}
      onMouseDown={(event: MouseEvent<HTMLButtonElement>) => {
        if (event.button !== 0) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        skipClick.current = true;
        window.setTimeout(() => {
          skipClick.current = false;
        }, 500);
        void toggle();
      }}
      onPress={() => {
        if (skipClick.current) {
          return;
        }
        void toggle();
      }}
    >
      <LockIcon filled={isPrivate} />
    </Pressable>
  );
}
